import type { LucideIcon } from 'lucide-react';
import { Menu, X } from 'lucide-react';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router';

import { Button } from './components/button.tsx';

export interface SidebarItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /** Count pill on the right; hidden when undefined. */
  badge?: number | undefined;
  end?: boolean;
}

export interface SidebarGroup {
  label?: string;
  items: SidebarItem[];
}

/**
 * Dashboard sidebar from the design system: logo mark + product label, grouped
 * nav (active item uses the graphite sidebar-primary pair), footer slot.
 * Below md it's an off-canvas drawer behind a hamburger top bar, closing on
 * backdrop click, Escape, or route change; at md+ it's the static in-flow
 * column it always was.
 */
export function Sidebar({
  logoSrc,
  productLabel,
  groups,
  footer,
}: {
  logoSrc: string;
  productLabel: string;
  groups: SidebarGroup[];
  footer?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  // Route change (nav link click, back/forward) always closes the drawer.
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = 'hidden';
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const nav = (
    <nav aria-label="Main" className="flex flex-1 flex-col gap-4 overflow-y-auto px-3 pt-6 pb-4">
      {groups.map((g, i) => (
        <div key={g.label ?? i} className="flex flex-col gap-0.5">
          {g.label && (
            <span className="px-2 pb-1 text-xs font-medium text-muted-foreground">{g.label}</span>
          )}
          {g.items.map(({ to, label, icon: Icon, badge, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end ?? false}
              className={({ isActive }) =>
                `flex items-center gap-2 rounded-md px-2 py-2 text-sm font-medium no-underline transition-colors ${
                  isActive
                    ? 'bg-sidebar-primary text-sidebar-primary-foreground'
                    : 'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
                }`
              }
            >
              <Icon aria-hidden="true" className="size-4 shrink-0" />
              <span className="flex-1 truncate">{label}</span>
              {badge !== undefined && (
                <span className="text-xs tabular-nums opacity-70">{badge}</span>
              )}
            </NavLink>
          ))}
        </div>
      ))}
    </nav>
  );

  return (
    <>
      <div className="sticky top-0 z-30 flex shrink-0 items-center gap-3 border-b border-sidebar-border bg-sidebar px-4 py-3 text-sidebar-foreground md:hidden">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label="Open menu"
          aria-expanded={open}
          onClick={() => setOpen(true)}
        >
          <Menu aria-hidden="true" />
        </Button>
        <img src={logoSrc} alt="" className="block size-6 shrink-0" />
        <span className="font-serif text-sm leading-tight font-medium">Remote Medium</span>
      </div>

      {open && (
        <div
          className="fixed inset-0 z-40 bg-black/40 md:hidden"
          aria-hidden="true"
          onClick={() => setOpen(false)}
        />
      )}

      <div
        className={`fixed inset-y-0 left-0 z-50 flex w-72 max-w-[85vw] shrink-0 -translate-x-full flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground transition-transform duration-200 ease-out md:sticky md:top-0 md:z-auto md:h-screen md:w-64 md:translate-x-0 ${
          open ? 'translate-x-0' : ''
        }`}
      >
        <div className="flex items-center justify-between gap-3 border-b border-border px-5 py-6">
          <div className="flex min-w-0 items-center gap-3">
            <img src={logoSrc} alt="" className="block size-8 shrink-0" />
            <div className="flex min-w-0 flex-col">
              <span className="font-serif text-base leading-tight font-medium">Remote Medium</span>
              <span className="text-xs text-muted-foreground">{productLabel}</span>
            </div>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="md:hidden"
            aria-label="Close menu"
            onClick={() => setOpen(false)}
          >
            <X aria-hidden="true" />
          </Button>
        </div>
        {nav}
        {footer && <div className="border-t border-sidebar-border px-4 py-4">{footer}</div>}
      </div>
    </>
  );
}
